import { createContext, useContext, useEffect, useState } from 'react';
import {
  type User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  AuthError,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { auth, db } from '../services/firebase';
import type { User } from '../types';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  isRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper function to format Firebase auth errors
function getAuthErrorMessage(error: any): string {
  if (error.code) {
    switch (error.code) {
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please check your credentials.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled. Please contact support.';
      case 'auth/weak-password':
        return 'Password is too weak. Please choose a stronger password.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      default:
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  }
  return error.message || 'An unexpected error occurred. Please try again.';
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function login(email: string, password: string) {
    try {
      toast.loading('Signing in...', { id: 'auth-login' });
      
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Immediately check admin privileges before proceeding
      toast.loading('Verifying admin privileges...', { id: 'auth-login' });
      
      const userDoc = await getDoc(doc(db, 'adminUsers', result.user.uid));
      if (!userDoc.exists()) {
        await signOut(auth);
        toast.error('Access denied. Admin privileges required.', { id: 'auth-login' });
        throw new Error('Access denied. Admin privileges required.');
      }
      
      const userData = userDoc.data() as User;
      
      // Validate user account status
      if (!userData.isActive) {
        await signOut(auth);
        toast.error('Account is deactivated. Please contact administrator.', { id: 'auth-login' });
        throw new Error('Account is deactivated. Please contact administrator.');
      }
      
      // Validate user has a valid role
      const validRoles = ['super_admin', 'manager', 'support', 'viewer'];
      if (!userData.role || !validRoles.includes(userData.role)) {
        await signOut(auth);
        toast.error('Invalid user role. Please contact administrator.', { id: 'auth-login' });
        throw new Error('Invalid user role. Please contact administrator.');
      }
      
      // All checks passed - set user profile and show success
      setUserProfile(userData);
      toast.success(`Welcome back, ${userData.name}!`, { id: 'auth-login' });
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = getAuthErrorMessage(error);
      toast.error(errorMessage, { id: 'auth-login' });
      throw error;
    }
  }

  async function logout() {
    try {
      toast.loading('Signing out...', { id: 'auth-logout' });
      await signOut(auth);
      setUserProfile(null);
      toast.success('Signed out successfully', { id: 'auth-logout' });
    } catch (error: any) {
      console.error('Logout error:', error);
      const errorMessage = getAuthErrorMessage(error);
      toast.error(`Failed to sign out: ${errorMessage}`, { id: 'auth-logout' });
      throw error;
    }
  }

  async function resetPassword(email: string) {
    try {
      toast.loading('Sending password reset email...', { id: 'auth-reset' });
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent! Check your inbox.', { id: 'auth-reset' });
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMessage = getAuthErrorMessage(error);
      toast.error(errorMessage, { id: 'auth-reset' });
      throw error;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    try {
      toast.loading('Changing password...', { id: 'auth-change-password' });
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated.');
      }
      const credential = EmailAuthProvider.credential(user.email!, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      toast.success('Password changed successfully!', { id: 'auth-change-password' });
    } catch (error: any) {
      console.error('Password change error:', error);
      const errorMessage = getAuthErrorMessage(error);
      toast.error(errorMessage, { id: 'auth-change-password' });
      throw error;
    }
  }

  function hasPermission(permission: string): boolean {
    if (!userProfile) return false;
    
    // Super admin has all permissions
    if (userProfile.role === 'super_admin') return true;
    
    // Define role-based permissions
    const rolePermissions: Record<string, string[]> = {
      manager: [
        'products.read', 'products.write', 'products.delete',
        'orders.read', 'orders.write',
        'customers.read', 'customers.write',
        'analytics.read',
        'content.read', 'content.write',
        'marketing.read', 'marketing.write',
        'inventory.read', 'inventory.write',
        'rushd.read', 'rushd.write'
      ],
      support: [
        'orders.read', 'orders.write',
        'customers.read', 'customers.write',
        'products.read',
        'rushd.read'
      ],
      viewer: [
        'analytics.read',
        'orders.read',
        'customers.read',
        'products.read',
        'rushd.read'
      ]
    };
    
    const permissions = rolePermissions[userProfile.role] || [];
    return permissions.includes(permission);
  }

  function isRole(role: string): boolean {
    return userProfile?.role === role;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'adminUsers', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            
            // Validate user account status and role on auth state change
            const validRoles = ['super_admin', 'manager', 'support', 'viewer'];
            if (!userData.isActive || !userData.role || !validRoles.includes(userData.role)) {
              // User account is invalid - sign them out silently
              await signOut(auth);
              setUserProfile(null);
              return;
            }
            
            setUserProfile(userData);
          } else {
            // User exists in Firebase Auth but not in admin collection
            await signOut(auth);
            setUserProfile(null);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    login,
    logout,
    resetPassword,
    changePassword,
    hasPermission,
    isRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 